import { useResolutionInfo } from '@mm/hooks';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { LeadsSearchQuery } from '@mm/state/interfaces/leads';
import { ADDON_SETTINGS_KEYS } from '@mm/state/interfaces/subscription';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { getCurrentSubscription } from '@mm/state/selectors/subscriptionSelectors';
import React, { useEffect, useState } from 'react';
import {
  selectFreezedCreditsSum,
  selectPurchasableLeadsCount,
} from '@mm/state/selectors/leadsSearchSelectors';
import { abbreviateNumber } from '@mm/utils';
import { Divider, message } from 'antd';
import { useSelector } from 'react-redux';
import { TABLET_SMALL } from '../../../../../constants/screenSize';
import styles from './LeadsSearch.module.css';
import { LeadsSearchFilterBar } from './LeadsSearchFilterBar/LeadsSearchFilterBar';
import { LeadsSearchFiltersMenu } from './LeadsSearchFiltersMenu/LeadsSearchFiltersMenu';
import { PurchaseContactsTable } from './PurchaseContactsTable/PurchaseContactsTable';
import classNames from 'classnames';

export const LeadsSearch = () => {
  const [filterParams, setFilterParamsBase] = useState<LeadsSearchQuery>({
    query: [],
    exclude: [],
    offset: 0,
  });
  const hasFilters = !!(
    filterParams.exclude?.length || filterParams.query.length
  );
  const freezedCreditsSum = useSelector(selectFreezedCreditsSum);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const leadsCount = useSelector(selectPurchasableLeadsCount);

  const creditsBalance = Math.floor(
    Number(facebookPage?.outgoingMessagesLimit) -
      Number(facebookPage?.outgoingMessageUsage?.subscriptionMessagingCount) -
      freezedCreditsSum,
  );

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;
  const [messageApi, contextHolder] = message.useMessage();
  const [isFirstTimeFiltersSet, setIsFirstTimeFiltersSet] = useState(false);

  const currentSubscription = useSelector(
    getCurrentSubscription(facebookPage?.id),
  );
  const { getSubscription } = useSubscriptionCRUDActions();
  const isNewBillingSubscription =
    facebookPage?.subscription &&
    isBilling2023Subscription(facebookPage?.subscription);
  const mercuryLeadsLeft =
    (currentSubscription?.addonUsage?.[ADDON_SETTINGS_KEYS.mercuryLeads]
      ?.included || 0) -
    (currentSubscription?.addonUsage?.[ADDON_SETTINGS_KEYS.mercuryLeads]
      ?.used || 0);

  const availableLeads = mercuryLeadsLeft > 0 ? mercuryLeadsLeft : 0;

  const setFilterParams = (params: LeadsSearchQuery) => {
    if (isMobile && !isFirstTimeFiltersSet) {
      messageApi.success(
        'Filters have been applied. Scroll down to see the results.',
      );
      setIsFirstTimeFiltersSet(true);
    }
    return setFilterParamsBase(params);
  };

  useEffect(() => {
    if (
      !currentSubscription &&
      facebookPage?.subscription &&
      isNewBillingSubscription
    ) {
      getSubscription(facebookPage?.subscription.id, facebookPage?.id);
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        {!isMobile && (
          <div>
            Consumer Directory
            <div className={styles.textSmall}>Select filters to find leads</div>
          </div>
        )}
        {isNewBillingSubscription ? (
          <div className={styles.creditsBalance}>
            Consumer Directory Leads available:{' '}
            {abbreviateNumber(availableLeads)}
          </div>
        ) : (
          <div className={styles.creditsBalance}>
            Credits balance: {abbreviateNumber(creditsBalance)}
          </div>
        )}
      </div>
      {contextHolder}

      <div
        className={classNames(styles.content, {
          [styles.contentEmptyState]: !leadsCount,
        })}
      >
        <LeadsSearchFiltersMenu
          filterParams={filterParams}
          setFilterParams={setFilterParams}
        />
        <div className={styles.contacts}>
          <LeadsSearchFilterBar
            filterParams={filterParams}
            setFilterParams={setFilterParams}
          />

          {hasFilters && <Divider style={{ margin: '12px 0px 22px' }} />}

          <PurchaseContactsTable
            filterParams={filterParams}
            setFilterParams={setFilterParams}
          />
        </div>
      </div>
    </div>
  );
};
